/**
 * SellerRentalsTab — two stacked panels:
 *   1. Per-product rental config (toggle + per-day + deposit + min/max days)
 *   2. Incoming reservations list with "Mark returned" action.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Calendar, Check, Power, PowerOff, RotateCcw, MapPin } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import SellerRentalsDashboard from "./SellerRentalsDashboard";
import RentalPriceOverridesEditor from "./RentalPriceOverridesEditor";


function ProductRentalRow({ product, onSaved }) {
  const [cfg, setCfg] = useState({
    rental_enabled: !!product.rental_enabled,
    rental_per_day: product.rental_per_day || "",
    rental_deposit: product.rental_deposit || "",
    rental_min_days: product.rental_min_days || 1,
    rental_max_days: product.rental_max_days || 30,
    rental_price_overrides: product.rental_price_overrides || [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const cleanOverrides = (cfg.rental_price_overrides || [])
        .filter(o => o.start_date && o.end_date && o.per_day)
        .map(o => ({
          start_date: o.start_date,
          end_date: o.end_date,
          per_day: parseFloat(o.per_day),
          label: o.label || "",
        }));
      await api.patch(`/seller/product/${product.product_id}/rental`, {
        rental_enabled: cfg.rental_enabled,
        rental_per_day: cfg.rental_per_day ? parseFloat(cfg.rental_per_day) : 0,
        rental_deposit: cfg.rental_deposit ? parseFloat(cfg.rental_deposit) : 0,
        rental_min_days: parseInt(cfg.rental_min_days || 1),
        rental_max_days: parseInt(cfg.rental_max_days || 30),
        rental_price_overrides: cleanOverrides.length ? cleanOverrides : null,
      });
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="card p-3"
      data-testid={`seller-rental-row-${product.product_id}`}>
      <div className="flex items-start gap-3">
        <img src={product.image} className="w-14 h-14 rounded-lg object-cover shrink-0" alt=""/>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm line-clamp-1">{product.name}</div>
          <div className="text-[10px] text-[#A1A1AA]">${product.price} list · {cfg.rental_enabled ? "On rent" : "Sale only"}</div>
        </div>
        <button onClick={() => set("rental_enabled", !cfg.rental_enabled)}
          data-testid={`seller-rental-toggle-${product.product_id}`}
          className={`shrink-0 px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-widest font-bold transition ${
            cfg.rental_enabled ? "bg-[#00F0FF] text-black" : "bg-[#3D2845] text-[#A1A1AA] hover:text-[#FAFAFA]"
          }`}>
          {cfg.rental_enabled ? <><Check size={11} className="inline mr-1"/>Rentable</> :
                                <><Power size={11} className="inline mr-1"/>Enable rent</>}
        </button>
      </div>

      {cfg.rental_enabled && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Per day $</span>
            <input type="number" min="0" step="1" value={cfg.rental_per_day}
              data-testid={`seller-rental-perday-${product.product_id}`}
              onChange={e => set("rental_per_day", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Deposit $</span>
            <input type="number" min="0" step="1" value={cfg.rental_deposit}
              data-testid={`seller-rental-deposit-${product.product_id}`}
              onChange={e => set("rental_deposit", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Min days</span>
            <input type="number" min="1" max="365" value={cfg.rental_min_days}
              onChange={e => set("rental_min_days", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Max days</span>
            <input type="number" min="1" max="365" value={cfg.rental_max_days}
              onChange={e => set("rental_max_days", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
        </div>
      )}

      {cfg.rental_enabled && (
        <RentalPriceOverridesEditor
          overrides={cfg.rental_price_overrides}
          onChange={(arr) => set("rental_price_overrides", arr)}
          basePerDay={cfg.rental_per_day}
        />
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        {err && <div className="text-[10px] text-[#FF007F] flex-1">{err}</div>}
        <button onClick={save} disabled={busy}
          data-testid={`seller-rental-save-${product.product_id}`}
          className="btn-primary text-[11px] px-3 py-1.5 ml-auto">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}


function ReservationRow({ rsv, onReturned }) {
  const [damage, setDamage] = useState(0);
  const [busy, setBusy] = useState(false);

  const markReturn = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/rentals/${rsv.reservation_id}/return`,
        null, { params: { damage_charge: damage || 0 } });
      onReturned?.();
    } catch (e) { alert(e?.response?.data?.detail || "Return failed"); }
    finally { setBusy(false); }
  };

  const statusColor = {
    confirmed: "#00F0FF", active: "#D4FF00",
    returned: "#A1A1AA", cancelled: "#FF007F",
  }[rsv.status] || "#FAFAFA";

  return (
    <div className="card p-3"
      data-testid={`seller-rsv-${rsv.reservation_id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">#{rsv.reservation_id.slice(-8).toUpperCase()}</div>
          <div className="text-[10px] text-[#A1A1AA]">
            {rsv.buyer?.name} · {rsv.buyer?.email}
          </div>
        </div>
        <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded"
          style={{ color: statusColor, background: `${statusColor}1A` }}>
          {rsv.status}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[10px] text-[#A1A1AA] mb-2">
        <Calendar size={10}/>{rsv.start_date} → {rsv.end_date} ({rsv.days} days)
        <span className="mx-1">·</span>
        <span className="text-[#D4FF00] font-bold">${rsv.rental_total}</span> rent
        <span className="mx-1">+</span>
        <span className="text-[#00F0FF] font-bold">${rsv.deposit_total}</span> dep
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {(rsv.items || []).map(it => (
          <div key={it.product_id} className="flex items-center gap-1 bg-[#3D2845]/40 rounded-full pl-1 pr-2 py-0.5">
            <img src={it.image} className="w-4 h-4 rounded-full object-cover"/>
            <span className="text-[10px] truncate max-w-[140px]">{it.name}</span>
          </div>
        ))}
      </div>
      {rsv.status === "returned" ? (
        <div className="text-[10px] text-[#A1A1AA]">
          ✓ Returned · damage charged ${rsv.damage_charge || 0} · refunded ${rsv.deposit_refund || 0}
        </div>
      ) : rsv.status === "confirmed" || rsv.status === "active" ? (
        <div className="flex items-center gap-2">
          <input type="number" min="0" max={rsv.deposit_total} value={damage}
            onChange={(e) => setDamage(e.target.value)}
            placeholder="Damage $0" className="input-dark text-xs flex-1"/>
          <button onClick={markReturn} disabled={busy}
            data-testid={`seller-rsv-return-${rsv.reservation_id}`}
            className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1">
            <RotateCcw size={11}/>{busy ? "…" : "Mark returned"}
          </button>
        </div>
      ) : null}
    </div>
  );
}


export default function SellerRentalsTab({ flash }) {
  const [products, setProducts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);  // bumps after any mutation → refresh dashboard

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/seller/rentals/products"),
        api.get("/seller/rentals/reservations"),
      ]);
      setProducts(a.data || []);
      setReservations(b.data || []);
    } catch (e) {
      flash(formatApiError(e?.response?.data?.detail) || "Load failed", true);
    } finally { setLoading(false); }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const bumpAndReload = () => { setVersion(v => v + 1); load(); };

  if (loading) return <div className="card p-8 text-center text-[#A1A1AA]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="seller-rentals-tab">
      {/* Dashboard at top — KPIs + overdue table */}
      <SellerRentalsDashboard version={version}/>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold uppercase text-sm">Products on rent</div>
            <div className="text-[10px] text-[#A1A1AA]">
              {products.filter(p => p.rental_enabled).length} of {products.length} enabled
            </div>
          </div>
          <div className="space-y-2">
            {products.map(p => (
              <ProductRentalRow key={p.product_id} product={p}
                onSaved={() => { flash("Rental config saved."); bumpAndReload(); }}/>
            ))}
            {products.length === 0 && (
              <div className="card p-6 text-center text-sm text-[#A1A1AA]">
                No products yet. Add products first, then mark them rentable here.
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold uppercase text-sm">Incoming reservations</div>
            <div className="text-[10px] text-[#A1A1AA]">{reservations.length}</div>
          </div>
          <div className="space-y-2">
            {reservations.map(r => (
              <ReservationRow key={r.reservation_id} rsv={r}
                onReturned={() => { flash("Marked returned."); bumpAndReload(); }}/>
            ))}
            {reservations.length === 0 && (
              <div className="card p-6 text-center text-sm text-[#A1A1AA]">
                <MapPin size={20} className="mx-auto opacity-30 mb-2"/>
                No incoming reservations yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
